/**
 * ModulePanel - Shows selected adventure module info
 *
 * Displays when player's party has selected a module.
 * Shows module name, description, and banner image.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { Button } from '@/components/ui/button';
import { Map, ChevronDown, ChevronUp } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { useLobbyStore, useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { apiClient } from '@/lib/api';
import type { Module } from '@/lib/api';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import ManagedVideo from '@/components/common/ManagedVideo';
import { resolveModuleMediaPath } from '@/lib/media';
import { GameTimeDisplay } from '@/components/game/GameTimeDisplay';

interface ModulePanelProps {
  className?: string;
}

export default function ModulePanel({ className }: ModulePanelProps) {
  const [module, setModule] = useState<Module | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isExpanded, toggleExpanded] = usePanelCollapse('module');

  const selectedCharacter = useSelectedCharacter();
  const parties = useLobbyStore((state) => state.parties);

  // Find the party that the selected character is in
  const characterParty = selectedCharacter
    ? parties.find((p) => p.memberCharacterIds.includes(selectedCharacter.id))
    : null;

  const selectedModuleId = characterParty?.selectedModuleId;

  // Did this party already complete this module? Drives the "COMPLETED"
  // badge overlaid on the hero video — read from `Party.completedModules`,
  // matching the currently-selected module by id.
  const isModuleCompleted = !!(
    selectedModuleId &&
    characterParty?.completedModules?.some((m) => m.moduleId === selectedModuleId)
  );

  // Fetch module data when selectedModuleId changes
  useEffect(() => {
    if (!selectedModuleId) {
      setModule(null);
      return;
    }

    const fetchModule = async () => {
      setIsLoading(true);
      setError(null);
      setMediaLoaded(false);
      try {
        const moduleData = await apiClient.getModule(selectedModuleId);
        setModule(moduleData);
      } catch (err) {
        console.error('Failed to fetch module:', err);
        setError('Failed to load module');
      } finally {
        setIsLoading(false);
      }
    };

    fetchModule();
  }, [selectedModuleId]);

  // Don't render if no module selected
  if (!selectedModuleId) {
    return null;
  }

  // SVG placeholder for module banner (16:9 aspect ratio)
  const PlaceholderBanner = () => (
    <svg
      viewBox="0 0 320 120"
      className="w-full h-auto rounded-md"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="320" height="120" className="fill-muted" rx="4" />
      <path
        d="M40 100 L80 70 L120 85 L160 50 L200 75 L240 55 L280 80 L280 100 Z"
        className="fill-muted-foreground/20"
      />
      <circle cx="260" cy="35" r="15" className="fill-muted-foreground/30" />
      <Map className="text-muted-foreground/40" x="140" y="40" width="40" height="40" />
    </svg>
  );

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Map className="h-4 w-4" />
            Adventure
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={toggleExpanded}
            aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="p-3 pt-0 space-y-2 select-none">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <AnkhSpinner size="lg" className="text-accent-gold" />
            </div>
          ) : error ? (
            <p className="text-xs text-destructive">{error}</p>
          ) : module ? (
            <>
              {/* Module Banner */}
              <div className="relative overflow-hidden rounded-md">
                {module.heroVideo ? (
                  <ManagedVideo
                    src={resolveModuleMediaPath(module.heroVideo)}
                    className={`w-full aspect-[16/9] object-cover rounded-md transition-opacity duration-300 ${
                      mediaLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoadedData={() => setMediaLoaded(true)}
                    onError={() => setMediaLoaded(false)}
                  />
                ) : module.heroImage ? (
                  <img
                    src={resolveModuleMediaPath(module.heroImage)}
                    alt={module.name}
                    className={`w-full aspect-[16/9] object-cover transition-opacity duration-300 ${
                      mediaLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setMediaLoaded(true)}
                    onError={() => setMediaLoaded(false)}
                  />
                ) : (
                  <PlaceholderBanner />
                )}
                {isModuleCompleted && (
                  <StatusPill className="absolute top-2 right-2 bg-emerald-600 text-white shadow-md gap-1 pointer-events-none">
                    <span aria-hidden className="text-sm leading-none">
                      👑
                    </span>
                    COMPLETED
                  </StatusPill>
                )}
              </div>

              {/* Module Info */}
              <div className="space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-medium">{module.name}</h3>
                  <StatusPill className="bg-secondary text-secondary-foreground shrink-0">
                    LEVEL {module.level.min}-{module.level.max}
                  </StatusPill>
                </div>
                <p className="text-sm text-foreground/70 line-clamp-2">{module.shortDescription}</p>
                {characterParty?.lastSessionEnd?.finalGameTimeSeconds != null && (
                  <GameTimeDisplay seconds={characterParty.lastSessionEnd.finalGameTimeSeconds} />
                )}
              </div>
            </>
          ) : (
            <p className="text-xs text-foreground">Loading module...</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
